import { EmailSignatureV2, TeamEmailSignatureBulkCreateRequest } from '@components/EmailSignaturev2/types';
import {
  sanitizeForDb,
  toDBEmailSignatureV2,
  toDBTeamEmailSignatureBulkCreateRequest,
  toEmailSignatureV2,
  toTeamEmailSignatureBulkCreateRequest,
} from '@modules/transformers';
import { assertIsType, assertIsTypedArray } from '@modules/types';
import firebase from 'firebase';
import {
  isDBEmailSignatureV2,
  isDBTeamEmailSignatureBulkCreateRequest,
} from './predicates/email-signature-predicates';
import { logger } from '@modules/logger';

export async function saveEmailSignature(emailSignature: EmailSignatureV2): Promise<void> {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const docRef = collectionRef.doc(emailSignature.id);

  const dbEmailSignature = toDBEmailSignatureV2(emailSignature);
  const sanitisedDbEmailSignature = sanitizeForDb(dbEmailSignature);
  return docRef.set(sanitisedDbEmailSignature, { merge: false });
}

export function subscribeToEmailSignature(
  emailSignatureId: string,
  onUpdate: (emailSignature: EmailSignatureV2) => void
): () => void {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const subscribe = collectionRef.doc(emailSignatureId).onSnapshot((snapshot) => {
    if (!snapshot.exists) return;

    const emailSignature = snapshot.data();
    assertIsType(emailSignature, isDBEmailSignatureV2);
    onUpdate(toEmailSignatureV2(emailSignature));
  });

  return subscribe;
}

export async function createTeamEmailSignatureBulkCreateRequest(
  emailSignatureBulkCreateRequest: TeamEmailSignatureBulkCreateRequest
): Promise<void> {
  const collectionRef = firebase.firestore().collection('team_email_signature_requests');
  const docRef = collectionRef.doc(emailSignatureBulkCreateRequest.id);

  const dbTeamEmailSignatureBulkCreateRequest = toDBTeamEmailSignatureBulkCreateRequest(
    emailSignatureBulkCreateRequest
  );

  return docRef.set(dbTeamEmailSignatureBulkCreateRequest, { merge: false });
}

export function subscribeToTeamEmailSignatureBulkCreateRequest(
  requestId: string,
  onUpdate: (emailSignatureBulkCreateRequest: TeamEmailSignatureBulkCreateRequest) => void
): () => void {
  const collectionRef = firebase.firestore().collection('team_email_signature_requests');
  const subscribe = collectionRef.doc(requestId).onSnapshot((snapshot) => {
    if (!snapshot.exists) return;

    const emailSignatureBulkCreateRequest = snapshot.data();
    assertIsType(emailSignatureBulkCreateRequest, isDBTeamEmailSignatureBulkCreateRequest);
    onUpdate(toTeamEmailSignatureBulkCreateRequest(emailSignatureBulkCreateRequest));
  });

  return subscribe;
}

export function subscribeToUserEmailSignatures(
  userId: string,
  onUpdate: (emailSignatures: Array<EmailSignatureV2>) => void
): () => void {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const docsQuery = collectionRef.where('user_id', '==', userId);

  const unsubscribe = docsQuery.onSnapshot((snapshot) => {
    const emailSignatures = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(emailSignatures, isDBEmailSignatureV2);

    onUpdate(emailSignatures.map((emailSignature) => toEmailSignatureV2(emailSignature)));
  });

  return unsubscribe;
}

export function subscribeToAllBusinessEmailSignatures(
  orgId: string,
  onUpdate: (emailSignatures: Array<EmailSignatureV2>) => void
): () => void {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const docsQuery = collectionRef.where('org_id', '==', orgId);

  const unsubscribe = docsQuery.onSnapshot((snapshot) => {
    const emailSignatures = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(emailSignatures, isDBEmailSignatureV2);

    onUpdate(emailSignatures.map((emailSignature) => toEmailSignatureV2(emailSignature)));
  });

  return unsubscribe;
}

export function sendEmailSignatureBulkCreateCsv(
  emailSignature: EmailSignatureV2,
  requestId: string,
  orgId: string,
  recipientEmail: string
): Promise<void> {
  logger.info('Calling firebase function', { function: 'sendEmailSignatureBulkCreateCsv' });
  const sendEmailSignatureBulkCreateCsv = firebase
    .functions()
    .httpsCallable('sendEmailSignatureBulkCreateCsv');
  return new Promise((resolve, reject) => {
    sendEmailSignatureBulkCreateCsv({
      email_signature: toDBEmailSignatureV2(emailSignature),
      request_id: requestId,
      org_id: orgId,
      recipient_email: recipientEmail,
    })
      .then(() => resolve())
      .catch(reject);
  });
}

export function deleteEmailSignature(id: string): Promise<void> {
  const collectionRef = firebase.firestore().collection('email_signatures');

  return new Promise((resolve, reject) => {
    collectionRef
      .doc(id)
      .delete()
      .then(() => resolve())
      .catch(reject);
  });
}

/**
 * Send the signature to the user, this function is currently used for sending testing signature and sending signature for iphone setup
 * @param emailSignatureId
 * @param testEmailSignature If the test signature object is provided, the cloud function will generate the image and store it in temp folder before sending the email
 */
export function sendEmailSignatureToUser({
  emailSignatureId,
  testEmailSignature, // if testEmailSignature is provided the function will generate the image for the signature and then send the email
}: {
  emailSignatureId?: string;
  testEmailSignature?: EmailSignatureV2; // if testEmailSignature is provided the function will generate the image for the signature and then send the email
}): Promise<void> {
  logger.info('Calling firebase function', { function: 'sendEmailSignatureHtml' });
  const sendEmailSignatureHtml = firebase.functions().httpsCallable('sendEmailSignatureHtml');
  return new Promise((resolve, reject) => {
    sendEmailSignatureHtml({
      email_signature_id: emailSignatureId,
      test_email_signature: testEmailSignature ? toDBEmailSignatureV2(testEmailSignature) : undefined,
    })
      .then(() => resolve())
      .catch(reject);
  });
}

/**
 * Get email signatures by user's ID
 * @param id ID of the user
 */
export async function getEmailSignaturesByUserId(userId: string): Promise<EmailSignatureV2[]> {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const docsQuery = collectionRef.where('user_id', '==', userId);

  const snapshot = await docsQuery.get();
  const dbEmailSignatures = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(dbEmailSignatures, isDBEmailSignatureV2);

  return dbEmailSignatures.map((dbEmailSignature) => toEmailSignatureV2(dbEmailSignature));
}

/** Gets the count of all email signatures for an org. This includes personal email signatures which
 * are part of the org, i.e. has the org_id and a the user_id field.
 */
export async function getEmailSignatureCount(orgId: string): Promise<number> {
  const collectionRef = firebase.firestore().collection('email_signatures');
  const docsQuery = collectionRef.where('org_id', '==', orgId);

  const snapshot = await docsQuery.get();
  return snapshot.size;
}
